import React, { useState, useEffect, useRef } from 'react';
import { Store, ChevronDown, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';

// Guarda la tienda activa. Se usa localStorage (no sessionStorage) para que
// no se pierda al cerrar la pestana.
export function getTiendaActiva() {
  return localStorage.getItem('oamatech_tienda_activa')
      || sessionStorage.getItem('oamatech_tienda_activa')
      || null;
}
export function getTiendaNombre() {
  return localStorage.getItem('oamatech_tienda_nombre')
      || sessionStorage.getItem('oamatech_tienda_nombre')
      || null;
}

export default function SelectorTienda({ darkMode }) {
  const { usuario } = useAuth();
  const [tiendas, setTiendas] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [cambiando, setCambiando] = useState(false);
  const ref = useRef(null);

  const activaId = getTiendaActiva() || String(usuario?.tienda_id || '');
  const activaNombre = getTiendaNombre() || usuario?.tienda_nombre || '';

  useEffect(() => {
    api.get('/tiendas')
      .then(r => setTiendas((r.data.data || []).filter(t => t.activa !== 0)))
      .catch(() => {});
  }, []);

  // Cerrar al pulsar fuera
  useEffect(() => {
    const fuera = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, []);

  const cambiar = async (t) => {
    if (String(t.id) === String(activaId)) { setAbierto(false); return; }
    setCambiando(true);
    try {
      await api.post('/tiendas/cambiar', { tienda_id: t.id });
      // Se guarda en los dos sitios para no romper pantallas que aun leen sessionStorage
      localStorage.setItem('oamatech_tienda_activa', String(t.id));
      localStorage.setItem('oamatech_tienda_nombre', t.nombre);
      sessionStorage.setItem('oamatech_tienda_activa', String(t.id));
      sessionStorage.setItem('oamatech_tienda_nombre', t.nombre);
      toast.success(`Trabajando en ${t.nombre}`);
      // Recarga inmediata: asi ninguna pantalla se queda con datos de la tienda anterior
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo cambiar de tienda');
      setCambiando(false);
    }
  };

  // Si solo hay una tienda, no tiene sentido mostrar el selector
  if (tiendas.length <= 1) return null;

  const nombreCorto = (activaNombre || '')
    .replace(/^iService\s*(Reparaciones)?\s*/i, '')
    .trim() || activaNombre;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setAbierto(v => !v)}
        disabled={cambiando}
        title="Cambiar de tienda"
        className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-xl transition-all font-medium"
        style={{
          background: darkMode ? 'rgba(168,85,247,0.15)' : 'rgba(168,85,247,0.10)',
          border: '1px solid rgba(168,85,247,0.3)',
          color: darkMode ? '#c084fc' : '#7c3aed',
        }}>
        <span className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0"
              style={{ boxShadow: '0 0 6px #4ade80' }} />
        <Store size={14} className="flex-shrink-0" />
        <span className="hidden sm:inline max-w-[140px] truncate">
          {cambiando ? 'Cambiando...' : nombreCorto}
        </span>
        <ChevronDown size={13} className={clsx('transition-transform flex-shrink-0', abierto && 'rotate-180')} />
      </button>

      {abierto && (
        <div className="absolute right-0 mt-2 w-64 rounded-2xl shadow-xl z-50 overflow-hidden"
             style={{
               background: darkMode ? '#1a1a2e' : 'white',
               border: darkMode ? '1px solid rgba(168,85,247,0.25)' : '1px solid rgba(0,0,0,0.08)',
             }}>
          <div className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide"
               style={{
                 color: darkMode ? '#64748b' : '#9ca3af',
                 borderBottom: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #f3f4f6',
               }}>
            Cambiar de tienda
          </div>
          {tiendas.map(t => {
            const esActiva = String(t.id) === String(activaId);
            return (
              <button key={t.id} onClick={() => cambiar(t)} disabled={cambiando}
                className="w-full text-left px-4 py-3 text-sm flex items-center gap-3 transition-colors"
                style={{
                  background: esActiva ? (darkMode ? 'rgba(168,85,247,0.12)' : 'rgba(168,85,247,0.07)') : 'transparent',
                  color: darkMode ? '#e2e8f0' : '#111827',
                }}
                onMouseEnter={e => { if (!esActiva) e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.04)' : '#f9fafb'; }}
                onMouseLeave={e => { if (!esActiva) e.currentTarget.style.background = 'transparent'; }}>
                <span className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ background: t.color || '#a855f7' }} />
                <span className="flex-1 truncate">{t.nombre}</span>
                {esActiva && <Check size={15} className="text-green-500 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
